import { CHANGE_REQUIRES_APPROVAL, type RouteName, type RouteResponse } from '@lia/contracts';
import { useToast } from '@lia/ui';
import { useMutation, useQuery, useQueryClient, type QueryKey, type UseQueryOptions } from '@tanstack/react-query';
import { api, ApiError, type Input } from './api.js';

export type ApiQueryOptions<N extends RouteName> = Omit<UseQueryOptions<RouteResponse<N>, ApiError>, 'queryKey' | 'queryFn'>;

/** Typed query over a contract route. The key must be workspace-prefixed for workspace routes (see `wk`). */
export function useApiQuery<N extends RouteName>(queryKey: QueryKey, name: N, input?: Input<N>, options?: ApiQueryOptions<N>) {
  return useQuery<RouteResponse<N>, ApiError>({ queryKey, queryFn: () => api(name, input), ...options });
}

export interface ApiMutationOptions<N extends RouteName> {
  /** Query key prefixes to invalidate after success. */
  invalidate?: readonly QueryKey[];
  /** Success toast title (omit for no toast). */
  success?: string | ((data: RouteResponse<N>, vars: Input<N>) => string | null);
  /** Error toast title; the API message is the description. */
  errorTitle?: string;
  /** Skip the error toast (e.g. the form shows the error inline). Also skipped for field validation errors when `inlineValidation`. */
  toastOnError?: boolean;
  /** When true, 400/422 errors with field details are not toasted (the form maps them to fields). */
  inlineValidation?: boolean;
  onSuccess?: (data: RouteResponse<N>, vars: Input<N>) => void;
  onError?: (err: ApiError, vars: Input<N>) => void;
}

/** Typed mutation over a contract route with invalidation and success/failure toasts. */
export function useApiMutation<N extends RouteName>(name: N, opts: ApiMutationOptions<N> = {}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation<RouteResponse<N>, ApiError, Input<N>>({
    mutationFn: (vars) => api(name, vars),
    onSuccess: async (data, vars) => {
      await Promise.all((opts.invalidate ?? []).map((queryKey) => qc.invalidateQueries({ queryKey })));
      const title = typeof opts.success === 'function' ? opts.success(data, vars) : opts.success;
      if (title) toast({ title, tone: 'success' });
      opts.onSuccess?.(data, vars);
    },
    onError: (err, vars) => {
      const error = err instanceof ApiError ? err : new ApiError('server', 0, 'unknown', String((err as Error)?.message ?? err));
      // Launch gate §10.3: a Level B change on a project in production became a change request (not a failure).
      if (error.code === CHANGE_REQUIRES_APPROVAL) {
        void Promise.all((opts.invalidate ?? []).map((queryKey) => qc.invalidateQueries({ queryKey })));
        toast({ title: 'Cambio enviado a aprobación', description: `${error.message}. Se aplicará cuando lo aprueben los responsables comercial y técnico (pestaña «Salida a producción»).`, tone: 'info' });
        return;
      }
      const fieldErrors = error.kind === 'validation' && Array.isArray(error.details) && error.details.length > 0;
      const skip = opts.toastOnError === false || (opts.inlineValidation && fieldErrors);
      if (!skip) toast({ title: opts.errorTitle ?? 'No se pudo completar la acción', description: error.message, tone: 'danger' });
      opts.onError?.(error, vars);
    },
  });
}

export function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && err.kind === 'forbidden';
}

export function isNotFound(err: unknown): boolean {
  return err instanceof ApiError && err.kind === 'not_found';
}
